// VRG-734 — react-query hooks for GTM → Content marketing → VERAfied.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { VerafiedLinkState } from '@repo/types'

import { verafiedApi, type VerafiedEpisodeInput, type VerafiedGuestInput, type VerafiedSegmentInput } from 'services'

const KEY = ['internal', 'gtm', 'verafied'] as const

export const useVerafiedGuests = () =>
  useQuery({ queryKey: [...KEY, 'guests'], queryFn: () => verafiedApi.guests(), staleTime: 30_000, retry: false })

export const useVerafiedEpisodes = () =>
  useQuery({ queryKey: [...KEY, 'episodes'], queryFn: () => verafiedApi.episodes(), staleTime: 30_000, retry: false })

export const useVerafiedSegments = () =>
  useQuery({ queryKey: [...KEY, 'segments'], queryFn: () => verafiedApi.segments(), staleTime: 30_000, retry: false })

export const useVerafiedPeopleSearch = (q: string) =>
  useQuery({
    queryKey: [...KEY, 'people', q],
    queryFn: () => verafiedApi.people(q),
    enabled: q.trim().length >= 2,
    staleTime: 15_000,
    retry: false,
  })

/** Guests, episodes and segments reference each other, so any write refreshes all three. */
const useInvalidate = () => {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: KEY })
}

export const useVerafiedActions = () => {
  const onSuccess = useInvalidate()
  return {
    createGuest: useMutation({ mutationFn: (i: VerafiedGuestInput) => verafiedApi.createGuest(i), onSuccess }),
    updateGuest: useMutation({
      mutationFn: ({ id, input }: { id: string; input: VerafiedGuestInput }) => verafiedApi.updateGuest(id, input),
      onSuccess,
    }),
    deleteGuest: useMutation({ mutationFn: (id: string) => verafiedApi.deleteGuest(id), onSuccess }),
    uploadGuestPhoto: useMutation({
      mutationFn: ({ id, file }: { id: string; file: File }) => verafiedApi.uploadGuestPhoto(id, file),
      onSuccess,
    }),
    createEpisode: useMutation({ mutationFn: (i: VerafiedEpisodeInput) => verafiedApi.createEpisode(i), onSuccess }),
    updateEpisode: useMutation({
      mutationFn: ({ id, input }: { id: string; input: VerafiedEpisodeInput }) => verafiedApi.updateEpisode(id, input),
      onSuccess,
    }),
    deleteEpisode: useMutation({ mutationFn: (id: string) => verafiedApi.deleteEpisode(id), onSuccess }),
    setEpisodeGuest: useMutation({
      mutationFn: ({ episodeId, guestId, state }: { episodeId: string; guestId: string; state: VerafiedLinkState }) =>
        verafiedApi.setEpisodeGuest(episodeId, guestId, state),
      onSuccess,
    }),
    removeEpisodeGuest: useMutation({
      mutationFn: ({ episodeId, guestId }: { episodeId: string; guestId: string }) => verafiedApi.removeEpisodeGuest(episodeId, guestId),
      onSuccess,
    }),
    setEpisodeSegments: useMutation({
      mutationFn: ({ episodeId, segmentIds }: { episodeId: string; segmentIds: string[] }) =>
        verafiedApi.setEpisodeSegments(episodeId, segmentIds),
      onSuccess,
    }),
    createSegment: useMutation({ mutationFn: (i: VerafiedSegmentInput) => verafiedApi.createSegment(i), onSuccess }),
    updateSegment: useMutation({
      mutationFn: ({ id, input }: { id: string; input: VerafiedSegmentInput }) => verafiedApi.updateSegment(id, input),
      onSuccess,
    }),
    deleteSegment: useMutation({ mutationFn: (id: string) => verafiedApi.deleteSegment(id), onSuccess }),
    applySuggestions: useMutation({ mutationFn: (ids: string[]) => verafiedApi.applySuggestions(ids), onSuccess }),
    dismissSuggestions: useMutation({ mutationFn: (ids: string[]) => verafiedApi.dismissSuggestions(ids), onSuccess }),
    importSheets: useMutation({
      mutationFn: (input: { guests_csv?: string; episodes_csv?: string }) => verafiedApi.importSheets(input),
      onSuccess,
    }),
  }
}
